import { JSX, useState } from 'react';
import { NavLink as RouterLink, useLocation, matchPath } from 'react-router-dom';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, List, Collapse, ListItemText, ListItemIcon, ListItemButton, Typography } from '@mui/material';
import Iconify from './Iconify';
import palette from 'src/theme/palette';

interface NavItemProps {
  item: {
    title: string;
    path: string;
    icon: JSX.Element;
    activeIcon: JSX.Element;
    paths: string[];
    info?: JSX.Element;
    children?: NavItemProps['item'][];
  };
  active: (path: string) => boolean;
}

const ListItemStyle = styled(ListItemButton)(({ theme }) => ({
  position: 'relative',
  textTransform: 'capitalize',
  color: '#64748B',
  borderRadius: 8,
  minHeight: 38,
  padding: '4px 10px',
  marginBottom: '2px',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: alpha('#EDB42E', 0.08),
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 32,
  height: 32,
  minWidth: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 12,
  color: 'inherit',
  backgroundColor: '#F8FAFC',
  borderRadius: 7,
});

const ListItemTextStyle = styled(ListItemText)({
  '& .MuiListItemText-primary': {
    fontSize: '0.85rem',
    fontWeight: 500,
    letterSpacing: '0.15px',
  },
});

function NavItem({ item, active }: NavItemProps) {
  const { pathname } = useLocation();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children, activeIcon } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };
  const activeRootStyle = {
    color: '#EDB42E',
    fontWeight: 700,
  };

  const activeSubStyle = {
    color: '#1A1A1A',
    fontWeight: 700,
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle>{isActiveRoot ? activeIcon : icon}</ListItemIconStyle>
          <ListItemTextStyle disableTypography primary={title} />
          {info && info}
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 14, height: 14, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item) => {
              const { title, path } = item;
              const isActiveSub = active(path);

              return (
                <ListItemStyle
                  key={title}
                  // @ts-ignore
                  component={RouterLink}
                  to={path}
                  sx={{
                    ...(isActiveSub && activeSubStyle),
                    minHeight: 32,
                    pl: 4.5,
                  }}
                >
                  <ListItemIconStyle sx={{ width: 20, height: 20, minWidth: 20, mr: 1 }}>
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        display: 'flex',
                        borderRadius: '50%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'text.disabled',
                        transition: (theme) => theme.transitions.create('transform'),
                        ...(isActiveSub && {
                          transform: 'scale(2)',
                          bgcolor: 'primary.main',
                        }),
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemTextStyle disableTypography primary={title} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  const isCurrentActive = item.paths.some((singlePath) => matchPath(singlePath, pathname));

  return (
    <ListItemStyle
      // @ts-ignore
      component={RouterLink}
      to={path}
      sx={{
        ...(isCurrentActive && activeRootStyle),
      }}
    >
      <ListItemIconStyle sx={{ ...(isCurrentActive && { bgcolor: alpha('#EDB42E', 0.12) }) }}>
        {isCurrentActive ? activeIcon : icon}
      </ListItemIconStyle>
      <Typography
        sx={{
          fontSize: '0.85rem',
          fontWeight: isCurrentActive ? 700 : 500,
          color: isCurrentActive ? palette.secondary.main : '#475569',
          letterSpacing: '0.1px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {title}
      </Typography>
      {info && info}
    </ListItemStyle>
  );
}

interface NavSectionProps {
  navConfig: NavItemProps['item'][];
}

export default function NavSection({ navConfig, ...other }: NavSectionProps) {
  const { pathname } = useLocation();

  const match = (path: string) => {
    return pathname.startsWith(path);
  };

  return (
    <Box {...other}>
      <List disablePadding sx={{ px: 0, py: 0.5 }}>
        {navConfig.map((item) => (
          <NavItem key={item.title} item={item} active={match} />
        ))}
      </List>
    </Box>
  );
}
